<template>
  <div>

    <b-form-group
      label="Référence de colis à transférer"
      label-for="colis-reference-input"
      aria-label="Champ de saisie de la référence du colis à transférer"
      description="Saisissez ou scannez le N° BL ou l'ID externe du colis à ajouter au bon de transfert."
    >
      <b-input-group class="input-group-merge">
        <b-input-group-prepend is-text>
          <feather-icon
            icon="MaximizeIcon"
            aria-hidden="true"
          />
        </b-input-group-prepend>
        <b-form-input
          id="colis-reference-input"
          v-model="ref"
          placeholder="N° BL ou ID externe (scannez ou saisissez)"
          aria-label="Entrée de la référence du colis à transférer"
          @change="checkOrder"
        />
      </b-input-group>
      <span class="sr-only">Après avoir scanné ou saisi la référence, appuyez sur Entrée pour valider.</span>
    </b-form-group>
    <validation-observer ref="simpleRules">
      <!-- Tables de sélection des colis -->
      <div
        class="d-flex justify-content-between"
        aria-label="Sélection des colis à transférer"
      >
        <b-card aria-label="Colis disponibles pour transfert">
          <b-form-group
            v-if="user.role === 'admin' || user.role === 'superadmin'"
            label="Dépôt source"
            aria-label="Sélection du dépôt source"
            description="Choisissez le dépôt d'origine pour filtrer les colis disponibles."
          >
            <validation-provider
              #default="{ errors }"
              name="Dépôt"
              rules="required"
            >
              <v-select
                v-model="repository"
                :clearable="false"
                placeholder="Sélectionner un dépôt source"
                label="name"
                :options="Array.isArray(repositories) ? repositories : []"
                aria-label="Liste des dépôts source disponibles"
                @input="getToRepositories"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <span class="font-weight-bold">Nombre de colis disponibles : {{ orders.length }}</span>
          <vue-good-table
            :columns="columns"
            :rows="orders"
            :search-options="{ enabled: true, externalQuery: searchTerm }"
            aria-label="Tableau des colis disponibles pour transfert"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.isOrderReference">
                <span> {{ props.row.display_cross_tenant_barcode && props.row.cross_tenant_barcode ? props.row.cross_tenant_barcode : props.row.id }}</span><br>
                <b-badge
                  v-if="props.row.status === 18"
                  variant="info"
                  class="ml-1"
                  aria-label="Colis en planification"
                >Planification</b-badge>
                <span
                  v-if="props.row.is_an_exchange"
                  style="color:red ; font-weight: bold;"
                  aria-label="Colis d'échange"
                > Échange </span>
              </span>
              <!-- Colonne : Action -->
              <span v-else-if="props.column.field === 'action'">
                <span>
                  <feather-icon
                    :id="`invoice-row-${props.row.id}-show-icon`"
                    icon="CheckSquareIcon"
                    class="cursor-pointer mr-1"
                    size="16"
                    color="green"
                    aria-label="Ajouter ce colis au bon de transfert"
                    @click="addItem(props.row)"
                  />
                  <b-tooltip
                    title="Ajouter au bon de transfert"
                    class="cursor-pointer"
                    :target="`invoice-row-${props.row.id}-show-icon`"
                  />
                </span>
              </span>
              <span v-else-if="props.column.field === 'governorate'">
                {{ props.row.governorate ? props.row.governorate.name : '' }}
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </b-card>
        <b-card
          class="ml-1"
          aria-label="Colis sélectionnés pour transfert"
        >
          <b-form-group
            label="Dépôt destination"
            aria-label="Sélection du dépôt de destination"
            description="Choisissez le dépôt de destination pour le transfert."
          >
            <validation-provider
              #default="{ errors }"
              name="Dépôt destination"
              rules="required"
            >
              <v-select
                v-model="toRepository"
                :clearable="false"
                placeholder="Sélectionner un dépôt de destination"
                label="name"
                :options="Array.isArray(toRepositories) ? toRepositories : []"
                aria-label="Liste des dépôts de destination disponibles"
                @input="onDestinationRepositoryChange"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <span class="font-weight-bold">Nombre de colis sélectionnés : {{ selected.length }}</span>
          <vue-good-table
            :columns="columns"
            :rows="selected"
            :search-options="{ enabled: true, externalQuery: searchTerm }"
            aria-label="Tableau des colis sélectionnés pour transfert"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.isOrderReference">
                <span> {{ props.row.display_cross_tenant_barcode && props.row.cross_tenant_barcode ? props.row.cross_tenant_barcode : props.row.id }}</span><br>
                <b-badge
                  v-if="props.row.status === 18"
                  variant="info"
                  class="ml-1"
                  aria-label="Colis en planification"
                >Planification</b-badge>
                <span
                  v-if="props.row.is_an_exchange"
                  style="color:red ; font-weight: bold;"
                  aria-label="Colis d'échange"
                > Échange </span>
              </span>
              <!-- Colonne : Action -->
              <span v-else-if="props.column.field === 'action'">
                <span>
                  <feather-icon
                    :id="`selected-row-${props.row.id}-remove-icon`"
                    icon="XCircleIcon"
                    class="cursor-pointer mr-1"
                    size="16"
                    color="red"
                    aria-label="Retirer ce colis du bon de transfert"
                    @click="resetItem(props.row)"
                  />
                  <b-tooltip
                    title="Retirer du bon de transfert"
                    class="cursor-pointer"
                    :target="`selected-row-${props.row.id}-remove-icon`"
                  />
                </span>
              </span>
              <span v-else-if="props.column.field === 'governorate'">
                {{ props.row.governorate ? props.row.governorate.name : '' }}
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
          <b-form-group
            label="Livreur"
            aria-label="Sélection du livreur pour le transfert"
            description="Sélectionnez le livreur responsable du transfert."
          >
            <validation-provider
              #default="{ errors }"
              name="Livreur"
              rules="required"
            >
              <v-select
                v-model="deliveryMan"
                :clearable="false"
                placeholder="Sélectionner un livreur"
                label="username"
                :options="Array.isArray(deliveryMans) ? deliveryMans : []"
                aria-label="Liste des livreurs disponibles"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            label="Véhicule de livraison"
            aria-label="Sélection du véhicule pour le transfert"
            description="Sélectionnez le véhicule utilisé pour le transfert."
          >
            <validation-provider
              #default="{ errors }"
              name="Véhicule"
              rules="required"
            >
              <v-select
                v-model="selectedCar"
                :clearable="false"
                placeholder="Choisir un véhicule"
                label="serial_number"
                :options="Array.isArray(cars) ? cars : []"
                :reduce="car => car.id"
                aria-label="Liste des véhicules disponibles"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <div class="d-flex justify-content-end mt-2">
            <span id="save-transfer-btn-wrapper">
              <b-button
                ref="saveButton"
                variant="success"
                class="save-btn"
                :class="{ 'not-allowed': selected.length === 0 || !toRepository || !deliveryMan || !selectedCar }"
                aria-label="Créer le bon de transfert avec les colis sélectionnés"
                :disabled="selected.length === 0 || !toRepository || !deliveryMan || !selectedCar"
                @click="addTransferVoucher"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                Créer le bon de transfert
              </b-button>
              <b-tooltip
                v-if="selected.length === 0 || !toRepository || !deliveryMan || !selectedCar"
                target="save-transfer-btn-wrapper"
                triggers="hover focus"
                placement="top"
              >
                <template v-if="selected.length === 0 && !toRepository && !deliveryMan && !selectedCar">
                  Veuillez sélectionner au moins un colis, un dépôt de destination, un livreur et un véhicule.
                </template>
                <template v-else-if="selected.length === 0 && !toRepository && !deliveryMan">
                  Veuillez sélectionner au moins un colis, un dépôt de destination et un livreur.
                </template>
                <template v-else-if="selected.length === 0 && !toRepository && !selectedCar">
                  Veuillez sélectionner au moins un colis, un dépôt de destination et un véhicule.
                </template>
                <template v-else-if="selected.length === 0 && !deliveryMan && !selectedCar">
                  Veuillez sélectionner au moins un colis, un livreur et un véhicule.
                </template>
                <template v-else-if="!toRepository && !deliveryMan && !selectedCar">
                  Veuillez sélectionner un dépôt de destination, un livreur et un véhicule.
                </template>
                <template v-else-if="selected.length === 0 && !toRepository">
                  Veuillez sélectionner au moins un colis et un dépôt de destination.
                </template>
                <template v-else-if="selected.length === 0 && !deliveryMan">
                  Veuillez sélectionner au moins un colis et un livreur.
                </template>
                <template v-else-if="selected.length === 0 && !selectedCar">
                  Veuillez sélectionner au moins un colis et un véhicule.
                </template>
                <template v-else-if="!toRepository && !deliveryMan">
                  Veuillez sélectionner un dépôt de destination et un livreur.
                </template>
                <template v-else-if="!toRepository && !selectedCar">
                  Veuillez sélectionner un dépôt de destination et un véhicule.
                </template>
                <template v-else-if="!deliveryMan && !selectedCar">
                  Veuillez sélectionner un livreur et un véhicule.
                </template>
                <template v-else-if="selected.length === 0">
                  Veuillez sélectionner au moins un colis à transférer.
                </template>
                <template v-else-if="!toRepository">
                  Veuillez sélectionner un dépôt de destination.
                </template>
                <template v-else-if="!deliveryMan">
                  Veuillez sélectionner un livreur.
                </template>
                <template v-else>
                  Veuillez sélectionner un véhicule.
                </template>
              </b-tooltip>
            </span>
          </div>
        </b-card>
      </div>
      <span class="sr-only">Après avoir sélectionné les colis, cliquez sur "Créer le bon de transfert" pour valider l'opération.</span>
    </validation-observer>
  </div>
</template>

<script>
import {
  BCard, BButton, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BTooltip,

} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference, orderReferenceSearchValue } from '@/utils/orderReference'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { required } from '@validations'

export default {
  components: {
    BButton,
    ValidationProvider,
    ValidationObserver,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BTooltip,
    vSelect,
    BCard,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
      deliveryMans: [],
      deliveryMan: '',
      repositories: [],
      toRepositories: [],
      repository: '',
      toRepository: '',
      date: '01-10-2021',
      status: '',
      pageLength: 100,
      dir: false,
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'code',
          field: orderReferenceSearchValue,
          isOrderReference: true,
          filterOptions: {
            enabled: true,
            placeholder: 'chercher BL / ID externe',
          },
        },
        {
          label: 'nom',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher nom',
          },
        },
        {
          label: 'gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'adresse',
          field: 'address',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher adresse',
          },
        },
        {
          label: 'tel',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher tel',
          },
        },
      ],
      role: '',
      orders: [],
      cars: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      isLoading: false,
      selectedCar: null,
    }
  },
  created() {
    this.user = storeAuth.state.user
    this.getDeliveryMans()
    if (this.user.role === 'admin' || this.user.role === 'superadmin') {
      this.getRepositories()
    } else {
      this.getOrdersAuDepot()
      this.getToRepositories()
    }
    this.getCars()
  },
  methods: {
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    exists(id) {
      return this.items.some(el => el.code === Number(id))
    },
    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },
    async getRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'network error!')
        }, 1000)
      }
    },
    async getToRepositories() {
      await this.getOrdersAuDepot()
      this.selected = []
      try {
        const { data } = await axios.get('/api/repositories/')
        const repositories = data
        if (this.user.role === 'manager' || this.user.role === 'warehouse_operator') {
          // Exclude the repository assigned to the manager/warehouse_operator
          this.toRepositories = repositories.filter(element => element.id !== this.user.repository)
        } else {
          // Admin: exclude the selected source repository
          const sourceRepoId = this.repository && this.repository.id ? this.repository.id : null
          this.toRepositories = repositories.filter(element => element.id !== sourceRepoId)
        }
      } catch (error) {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'network error!')
        }, 1000)
      }
    },
    async checkOrder() {
      const searchRef = this.ref.trim()
      const matchOrder = element => orderMatchesReference(element, searchRef)
      const result = this.orders.find(matchOrder)
      const selected = this.selected.find(matchOrder)

      if (result) {
        // Check if destination repository is selected before adding scanned order
        if (!this.toRepository) {
          await this.withTitle('Veuillez d\'abord sélectionner un dépôt de destination avant de scanner des colis.')
          this.ref = ''
          await this.playAudio(this.notexist)
          return
        }

        // Check if destination depot has governorates assigned
        if (!this.toRepository.governorates || this.toRepository.governorates.length === 0) {
          await this.withTitle(
            'Erreur de destination : Le dépôt de destination n\'a aucun gouvernorat assigné. Veuillez configurer les gouvernorats du dépôt.',
          )
          this.ref = ''
          this.speakText('Erreur destination')
          return
        }

        // Check governorate compatibility for scanned orders
        if (result.governorate) {
          const destinationGovernorateIds = this.toRepository.governorates.map(gov => gov.id)
          if (!destinationGovernorateIds.includes(result.governorate.id)) {
            const destinationGovernorateNames = this.toRepository.governorates.map(gov => gov.name).join(', ')
            await this.withTitle(
              `Erreur de destination : Ce colis a pour gouvernorat "${result.governorate.name}" mais le dépôt de destination couvre les gouvernorats : ${destinationGovernorateNames}. Le gouvernorat du colis doit correspondre à l'un de ces gouvernorats.`,
            )
            this.ref = ''
            this.speakText('Erreur destination')
            return
          }
        }

        await this.addItem(result)
        this.ref = ''
        this.speakText('OK')
      } else if (selected) {
        await this.withTitle('Colis déja scanné')
        this.speakText('Colis déjà scanné')
      } else {
        this.speakText('Colis inexistant')
        this.ref = ''
      }
    },
    async addTransferVoucher() {
      if (this.$refs.saveButton.hasAttribute('disabled')) {
        return
      }
      const valid = await this.$refs.simpleRules.validate()
      if (valid) {
        this.$refs.saveButton.setAttribute('disabled', 'disabled')
        // Only set from_repository for admin/superadmin; backend auto-assigns for manager/warehouse_operator
        const fromRepository = (this.user.role === 'admin' || this.user.role === 'superadmin') ? this.repository.id : null
        const orderIds = this.selected.map(order => order.id)
        const payload = {
          delivery_man: this.deliveryMan.id,
          orders: orderIds,
          to_repository: this.toRepository.id,
          car: this.selectedCar,
        }
        if (fromRepository) payload.from_repository = fromRepository
        try {
          const { data } = await axios.post('/api/transfer-vouchers/create-transfer-voucher-orders/', payload)
          this.$router.push(`/bons-transferts/show/${data.transfer_voucher_id}`)
          this.showToast('success', 'top-center', 'Bon de transfert ajouté avec succès')
        } catch (error) {
          this.showToast('danger', 'top-center', error.toString())
        } finally {
          this.$refs.saveButton.removeAttribute('disabled')
        }
      } else {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'Champ obligatoire ! Vérifiez vos données !')
        }, 1000)
      }
    },
    async getDeliveryMans() {
      this.isLoading = true
      try {
        const { data } = await axios.get('api/auth/users/drivers/')
        this.deliveryMans = data
        this.isLoading = false
      } catch (error) {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'network error: ')
        }, 1000)
      }
    },
    async addItem(item) {
      // Check if destination repository is selected before adding order
      if (!this.toRepository) {
        await this.withTitle('Veuillez d\'abord sélectionner un dépôt de destination avant d\'ajouter des colis.')
        return
      }

      // Check if destination depot has governorates assigned
      if (!this.toRepository.governorates || this.toRepository.governorates.length === 0) {
        await this.withTitle(
          'Erreur de destination : Le dépôt de destination n\'a aucun gouvernorat assigné. Veuillez configurer les gouvernorats du dépôt.',
        )
        this.speakText('Erreur destination')
        return
      }

      // Check governorate compatibility if order has a governorate
      if (item.governorate) {
        const destinationGovernorateIds = this.toRepository.governorates.map(gov => gov.id)
        if (!destinationGovernorateIds.includes(item.governorate.id)) {
          const destinationGovernorateNames = this.toRepository.governorates.map(gov => gov.name).join(', ')
          await this.withTitle(
            `Erreur de destination : Ce colis a pour gouvernorat "${item.governorate.name}" mais le dépôt de destination couvre les gouvernorats : ${destinationGovernorateNames}. Le gouvernorat du colis doit correspondre à l'un de ces gouvernorats.`,
          )
          this.speakText('Erreur destination')
          return
        }
      }

      this.selected.push(item)
      const i = this.orders.findIndex(element => element.id === item.id)
      this.orders.splice(i, 1)
    },
    async resetItem(item) {
      this.orders.push(item)
      const i = this.selected.findIndex(element => element.id === item.id)
      this.selected.splice(i, 1)
    },
    onDestinationRepositoryChange() {
      // When destination repository changes, check if any selected orders have incompatible governorates
      if (this.toRepository && this.toRepository.governorates && this.toRepository.governorates.length > 0) {
        const destinationGovernorateIds = this.toRepository.governorates.map(gov => gov.id)
        const incompatibleOrders = this.selected.filter(order => order.governorate && !destinationGovernorateIds.includes(order.governorate.id))

        if (incompatibleOrders.length > 0) {
          // Move incompatible orders back to available orders
          incompatibleOrders.forEach(order => {
            this.resetItem(order)
          })

          // Show notification about removed orders
          const orderIds = incompatibleOrders.map(order => order.id).join(', ')
          const destinationGovernorateNames = this.toRepository.governorates.map(gov => gov.name).join(', ')
          this.showToast(
            'warning',
            'top-center',
            `Les colis ${orderIds} ont été retirés car leurs gouvernorats ne correspondent pas aux gouvernorats du dépôt de destination (${destinationGovernorateNames}).`,
          )
        }
      }
    },
    async getOrdersAuDepot() {
      this.isLoading = true
      let { repository } = this.user
      if (this.user.role === 'admin' || this.user.role === 'superadmin') {
        repository = this.repository.id
      }
      try {
        const { data } = await axios.get('/api/orders/eligible-for-transfer/', {
          params: {
            repository,
          },
        })
        this.orders = data
      } catch (error) {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'network error: ')
        }, 1000)
      }
    },
    async getCars() {
      this.isLoading = true
      try {
        const { data } = await axios.get('/api/cars/')
        this.cars = Array.isArray(data.results) ? data.results : []
        this.isLoading = false
      } catch (error) {
        this.showToast('danger', 'top-center', 'network error')
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

.save-btn.not-allowed,
.save-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}
</style>
